<script setup lang="ts">
import InterviewDialog from '../InterviewDialog.vue';
import InterviewFigure from '../InterviewFigure.vue';
import InterviewImpression from '../InterviewImpression.vue';
import IntervieweeSummary from '../IntervieweeSummary.vue';
import IconPlanner from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/InterviewTemplate/icon_planner.png/icon_planner.webp';
import { mobileMaxWidth } from 'theme/assets/styles/export.module.scss';

const props = defineProps<{ interviewee: Interviewee }>();

const PlannerDialog: Component<unknown> = (_: unknown | null, { slots }) =>
  h(
    InterviewDialog,
    { variant: 'planner', avatarUrl: IconPlanner },
    {
      default: (props: { clsNotice: string; clsEm: string }) =>
        slots.default?.(props),
    }
  );

const UserDialog: Component<unknown> = (_: unknown, { slots }) =>
  h(
    InterviewDialog,
    { variant: 'user', avatarUrl: props.interviewee.avatarUrl },
    {
      default: (props: { clsNotice: string; clsEm: string }) =>
        slots.default?.(props),
    }
  );
</script>

<template>
  <div :class="$style.container">
    <IntervieweeSummary
      :class="$style.container__summary"
      :interviewee="interviewee"
    />
    <PlannerDialog>
      Aさん、はじめまして！<br />
      事前に気になっている点を伺いまして、本日はまずライフプランを作成し、結果を踏まえてそれぞれのテーマについてお話しするのが良いと思うのですが、いかがでしょうか？
    </PlannerDialog>

    <UserDialog>
      そうですね、まさにそのようなイメージをしておりました。ありがとうございます。
    </UserDialog>

    <PlannerDialog>
      ライフプランを作成する前にお伺いしたいのですが、教育資金を目的に定期預金や学資保険、あるいはつみたてNISAなど何かやっていらっしゃることはありますか？
    </PlannerDialog>

    <UserDialog v-slot="{ clsNotice }">
      今はないですね。教育資金目的ではないですが、つみたてNISAは上限の3万3000円を毎月やっているんですけど…。保険も何も入っていないので、ちょっとそこも考えていきたいと思っています。
      <span :class="clsNotice">※2023年10月時点</span>
    </UserDialog>

    <PlannerDialog>
      かしこまりました！では早速ヒアリングしながらライフプランを作成していきますね。
    </PlannerDialog>

    <UserDialog> よろしくお願いいたします。 </UserDialog>

    <InterviewFigure
      :class="$style.container__figures"
      caption="Aさんのライフプラン"
    >
      <div :class="$style.figure">
        <div :class="$style.listing">
          <div :class="$style.listing__caption">ピックアップ</div>
          <ul :class="$style.listing__listing">
            <li>夫婦共働きの会社員、世帯としては1,000万円以上の収入がある</li>
            <li>4ヶ月前に生まれた双子の子供がいる</li>
            <li>
              奥様は現在育児休業中。子供が1歳になったらフルタイムで復職する予定
            </li>
            <li>賃貸に住んでいるが3年後くらいにはマンションを購入したい</li>
            <li>前職の持ち株と2年前からつみたてNISAを運用中</li>
            <li>ローンはいっさいなし</li>
          </ul>
        </div>
      </div>
      <div :class="$style.figure">
        <picture>
          <source
            srcset="./fig_1_1__sp.png"
            :media="`(max-width: ${mobileMaxWidth})`"
          />
          <img :class="$style.figure__img" src="./fig_1_1.png" />
        </picture>
      </div>
    </InterviewFigure>

    <PlannerDialog>
      結論を申し上げると、現状大きく問題はなさそうですね。仮に今回想定していらっしゃる住宅購入資金が発生しても資産がマイナスになることはなさそうです。教育資金についても、高校からお子様お二人とも私立の学校に進学して、かつ大学も私立理系で仕送りが発生する場合だったとしても全然大丈夫そうですよ。
    </PlannerDialog>

    <UserDialog>
      では35年ローンでマンションをドーンと買うぐらいだったら、まぁ無理はないのかなっていう感じなのですね、なるほどなるほど。
    </UserDialog>

    <PlannerDialog v-slot="{ clsEm }">
      ただ、このシミュレーションの中でまず改善点として、家計の「守り」が全然ないので、その点は検討されるといいですね。<br />年間の支出をみると、<span
        :class="clsEm"
        >ご夫婦どちらかお一人でもかけてしまうと、途端にプランが崩れます。</span
      >守り、つまり保険についてご説明いたしますね。
    </PlannerDialog>

    <InterviewFigure :class="$style.container__figures" caption="保険の考え方">
      <div :class="$style.figure">
        <picture>
          <source
            srcset="./fig_1_2__sp.svg"
            :media="`(max-width: ${mobileMaxWidth})`"
          />
          <img :class="$style.figure__img" src="./fig_1_2.svg" />
        </picture>
      </div>
    </InterviewFigure>

    <PlannerDialog v-slot="{ clsEm }">
      保険につきましては、まず何かあった時にどれだけの資金が必要かの試算が必要です。それを踏まえて、公的保険を考慮した上で不足する分を民間の保険で賄うイメージです。Aさんの場合はまずお子様が生まれたばかりなので、死亡保障は必須ですね。死亡保障には<span
        :class="clsEm"
        >収入保障保険</span
      >と<span :class="clsEm">定期保険</span
      >という2パターンがありまして、収入保障保険は年々額が減っていくいわゆる三角と言われるものです。一方、定期保険はいつお亡くなりになっても保障する額が変わらない、箱型と言われるものですね。
    </PlannerDialog>

    <UserDialog v-slot="{ clsEm }">
      なるほど、<span :class="clsEm"
        >生きながらえた分、つまり保障がいらなくなる分、額が減っていく保険</span
      >があるのですね！
    </UserDialog>

    <PlannerDialog v-slot="{ clsEm }">
      私からのご提案をすると、将来的に十分キャッシュがあるので、
      <span :class="clsEm">無駄に保険をかけずに今を手厚くされること</span
      >をお勧めします。あとAさんの場合、怖いのは働けなくなるリスクですね。
    </PlannerDialog>

    <InterviewFigure
      :class="$style.container__figures"
      caption="就業不能のリスク"
    >
      <div :class="$style.figure">
        <picture>
          <source
            srcset="./fig_1_3__sp.svg"
            :media="`(max-width: ${mobileMaxWidth})`"
          />
          <img :class="$style.figure__img" src="./fig_1_3.svg" />
        </picture>
      </div>
      <div :class="$style.figure">
        <picture>
          <source
            srcset="./fig_1_4__sp.svg"
            :media="`(max-width: ${mobileMaxWidth})`"
          />
          <img :class="$style.figure__img" src="./fig_1_4.svg" />
        </picture>
      </div>
    </InterviewFigure>

    <PlannerDialog v-slot="{ clsEm, clsNotice }">
      <p>
        死亡保障でカバーしきれない、病気や怪我で収入が減る場合に備えて入るものが、<span
          :class="clsEm"
          >就業不能保険</span
        >と言われるものです。<br />もし、働けなくなった場合、会社員は公的保障として傷病手当金を1年半は給料の約2/3の額がもらえます。その後も働けない場合は障害年金が支払われるのですが、障害年金は大体お給料の半分以下になります。その額でも生活できるのならいいのですが、ちょっとAさんの方が働けなくなったら厳しいですね…。<span
          :class="clsEm"
          >死亡保障と就業不能保障は優先的に</span
        >、ご検討いただいたほうがいいです。
      </p>
      <p style="line-height: 1">
        <span :class="clsNotice"
          >※公的保障の適用には所定の条件を満たす必要があります</span
        >
      </p>
    </PlannerDialog>

    <UserDialog>
      よくわかりました。ありがとうございます。素人考えでそうなんだろうなーって思ってたところとご提案が一致したので、違和感ないです。
    </UserDialog>

    <PlannerDialog> では今回の面談を振り返りますね。 </PlannerDialog>

    <InterviewFigure
      :class="$style.container__figures"
      caption="Aさんの面談振り返り"
    >
      <div :class="$style.figures__col">
        <div :class="$style.figure">
          <div :class="$style.listing">
            <div :class="$style.listing__caption">実践したこと</div>
            <ul :class="$style.listing__listing">
              <li>ライフプランシミュレーション</li>
              <li>保険の考え方のご説明</li>
            </ul>
          </div>
        </div>

        <div :class="$style.figure">
          <div :class="$style.listing">
            <div :class="$style.listing__caption">面談を通じて分かったこと</div>
            <ul :class="$style.listing__listing">
              <li>現状大きく問題はなし</li>
              <li>教育資金・住宅資金もご夫婦が健康なら十分に確保可能</li>
            </ul>
          </div>
        </div>
      </div>
      <div :class="$style.figure">
        <div :class="$style.listing">
          <div :class="$style.listing__caption">課題・改善・実施したい点</div>
          <ul :class="$style.listing__listing">
            <li>
              家計の守りが全くないので、<span :class="$style.listing__em"
                >死亡するリスク、働けなくなるリスクに対しては優先的に保険でカバーする</span
              >ことを検討する
            </li>
            <li>
              余剰資金分に関しては、<span :class="$style.listing__em"
                >投資を活用して</span
              >効率的な運用を
            </li>
          </ul>
        </div>
      </div>
    </InterviewFigure>

    <InterviewImpression
      :class="$style.container__impression"
      :avatar-url="interviewee.avatarUrl"
      notice="※ この記事は、2023年10月にお客様からご相談いただいた内容について、対象のお客様の許可を得て掲載しています ※本レビューの執筆に際し、謝礼をお渡ししました。この対価はレビューの執筆に対する感謝を表すものであり、レビューの内容や意見に影響を与えるものではありません ※内容は、個々の体験や意見に基づいています。お客様の個人的な意見であり、必ずしも全ての方に当てはまるものではありません"
      body="ライフプランをシミュレーションしてもらうことで、当初抱えていた不安はそこまで気にしなくても良いことがわかった一方で、万が一への備えが重要であることを教えていただきました。ライフプランをシミュレーションしてくださったアドバイザーさんから、そのまま保険専門のアドバイザーさんをご紹介いただいたのですが、ライフプランを前提とした提案をしてくださって、結果的に納得のいく保険選びに繋がりました。月日が経ち、今度は住宅購入を検討し始めたときにもライフプランシミュレーションの見直しから適切な予算等の相談に乗ってもらうことができ、テーマごとにいちから状況の説明をしなくて良いのでとてもありがたく思っています。"
    />
  </div>
</template>

<style module lang="scss">
.container {
  display: flex;
  flex-direction: column;
  gap: 24px;

  @include mobile-only {
    gap: 16px;
  }

  &__figures {
    margin: 8px 0;
  }

  &__impression {
    margin-top: 24px;
  }

  &__summary {
    margin-bottom: 24px;
  }
}

.figures {
  &__col {
    display: flex;
    flex-direction: column;

    gap: 8px;
  }
}

.figure {
  background-color: $color-surface-primary;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  &__img {
    max-height: 240px;
    display: block;
    @include mobile-only {
      max-height: unset;
      width: 100%;
    }
  }
}

.listing {
  width: 100%;
  height: 100%;
  line-height: 1.8;
  padding: 16px 24px;
  &__caption {
    font-weight: bold;
  }
  &__listing {
    margin-left: 20px;
    list-style-type: disc;
  }
  &__em {
    font-weight: bold;
    background: linear-gradient(transparent 0% 60%, $color-yellow-300 60%);
    padding-bottom: 2px;
  }
}
</style>
